import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../constants';
import { useTokens } from '../../context/TokenContext';
import { xorEncrypt } from '../../utils/xorCrypto';

const ChangePassword = ({ id }) => {
  const { getItem } = useTokens();
  const adminAuthToken = getItem('admin', 'AuthToken');
  const [loading, setLoading] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isPasswordMatch, setIsPasswordMatch] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    old_password: '',
    password: '',
    passwordConfirm: '',
  });

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'password') {
      const passwordRegex = /^.{8,}$/;
      setIsPasswordValid(passwordRegex.test(value));
      setIsPasswordMatch(value === formData.passwordConfirm);
    }

    if (name === 'passwordConfirm') {
      setIsPasswordMatch(value === formData.password);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid || !isPasswordMatch) return;

    setLoading(true);
    const toastId = toast.loading('Changing password...', { autoClose: false });

    try {
      const dataToSend = {
        old_password: xorEncrypt(formData.old_password),
        password: xorEncrypt(formData.password),
        passwordConfirm: xorEncrypt(formData.passwordConfirm),
      };

      const response = await axios.put(`${API_URL}/admin/change-password`, dataToSend, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminAuthToken}`,
        },
      });

      if (response.status === 200) {
        toast.update(toastId, {
          render: 'Password changed successfully!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
        setFormData({ old_password: '', password: '', passwordConfirm: '' });
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to change password.';
      toast.update(toastId, {
        render: msg,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="pb-1">Change User Password</h2>
      <p className="pb-3 small fst-italic">Update the user's password securely.</p>

      <form onSubmit={handleFormSubmit}>
        {/* Old Password */}
        <div className="mb-3">
          <label htmlFor="old_password" className="form-label">Old Password <span className="text-danger">*</span></label>
          <input
            type={showPassword ? 'text' : 'password'}
            className="form-control"
            id="old_password"
            name="old_password"
            value={formData.old_password}
            onChange={handleInputChange}
            required
            placeholder="Enter old password"
          />
        </div>

        {/* New Password */}
        <div className="mb-3">
          <label htmlFor="password" className="form-label">New Password <span className="text-danger">*</span></label>
          <div className="input-group">
            <input
              type={showPassword ? 'text' : 'password'}
              className={`form-control ${!isPasswordValid ? 'is-invalid' : ''}`}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              placeholder="Enter new password"
            />
            <span className="input-group-text" style={{ cursor: 'pointer' }} onClick={togglePasswordVisibility}>
              <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
            </span>
          </div>
          {!isPasswordValid && <div className="invalid-feedback">Password must be at least 8 characters long.</div>}
        </div>

        {/* Confirm Password */}
        <div className="mb-3">
          <label htmlFor="passwordConfirm" className="form-label">Confirm Password <span className="text-danger">*</span></label>
          <input
            type={showPassword ? 'text' : 'password'}
            className={`form-control ${!isPasswordMatch ? 'is-invalid' : ''}`}
            id="passwordConfirm"
            name="passwordConfirm"
            value={formData.passwordConfirm}
            onChange={handleInputChange}
            required
            placeholder="Confirm new password"
          />
          {!isPasswordMatch && <div className="invalid-feedback">Passwords do not match.</div>}
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Changing...' : 'Change Password'}
        </button>
      </form>
    </div>
  );
};

export default ChangePassword;
